import React from 'react';
import { Input, Switch } from 'antd';
import type { CommissionRule, LaneCard } from '../types';
import { LaneCardDisplay } from './LaneCardDisplay';
import { useDroppable } from '@dnd-kit/core';

interface RuleBlockProps {
  rule: CommissionRule;
  onRuleChange: (rule: CommissionRule) => void;
  onCardRemove: (lane: 'include' | 'exclude', cardId: string) => void;
  onCardConfigure: (lane: 'include' | 'exclude', card: LaneCard) => void;
}

export const RuleBlock: React.FC<RuleBlockProps> = ({
  rule,
  onRuleChange,
  onCardRemove, 
  onCardConfigure,
}) => {
  const updateRule = (patch: Partial<CommissionRule>) => {
    onRuleChange({ ...rule, ...patch });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateRule({ name: e.target.value });
  };

  const handleActiveChange = (checked: boolean) => {
    updateRule({ active: checked });
  };

  const { setNodeRef: setIncludeRef, isOver: isOverInclude } = useDroppable({
    id: `include-${rule.id}`,
    data: { lane: 'include', ruleId: rule.id },
  });

  const { setNodeRef: setExcludeRef, isOver: isOverExclude } = useDroppable({
    id: `exclude-${rule.id}`,
    data: { lane: 'exclude', ruleId: rule.id },
  });

  return (
    <div className="commission-rule-block">
      <div className="commission-rule-header">
        <Input
          placeholder="Name"
          value={rule.name}
          onChange={handleNameChange}
          style={{ flex: 1, maxWidth: 300 }}
        />
      </div>

      <div className="commission-rule-lanes commission-rule-lanes--muted">
        <div className="commission-lane commission-lane--panel">
          <div className="commission-lane-header">
            <span className="commission-lane-title">Include</span>
            <span className="commission-lane-sub">
              Select the products this commission applies to. All selected Include rules must match.
            </span>
          </div>
          <div
            ref={setIncludeRef}
            className={`commission-lane-dropzone ${isOverInclude ? 'commission-lane-dropzone-over' : ''}`}
          >
            {rule.include.map((card) => (
              <LaneCardDisplay
                key={card.id}
                card={card}
                lane="include"
                onRemove={() => {
                  if (!card.isMirror) onCardRemove('include', card.id);
                }}
                onConfigure={() => {
                  if (!card.isMirror) onCardConfigure('include', card);
                }}
              />
            ))}
          </div>
        </div>

        <div className="commission-lane commission-lane--panel">
          <div className="commission-lane-header">
            <span className="commission-lane-title">Exclude</span>
            <span className="commission-lane-sub">
              Select products that should be left out. If a product matches an Exclude rule, the commission will not
              apply to that product.
            </span>
          </div>
          <div
            ref={setExcludeRef}
            className={`commission-lane-dropzone ${isOverExclude ? 'commission-lane-dropzone-over' : ''}`}
          >
            {rule.exclude.map((card) => (
              <LaneCardDisplay
                key={card.id}
                card={card}
                lane="exclude"
                onRemove={() => {
                  if (!card.isMirror) onCardRemove('exclude', card.id);
                }}
                onConfigure={() => {
                  if (!card.isMirror) onCardConfigure('exclude', card);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="commission-rule-active">
        <Switch
          checked={rule.active}
          onChange={handleActiveChange}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      </div>
    </div>
  );
};